/** Shared rule-sync / tag-wipe toast + sessionStorage keys (Tx Review + app-wide background jobs). */

export const AFTER_TAG_WIPE_RELOAD_MSG_KEY = 'ff_after_tag_wipe_success_v1';
export const AFTER_TAG_WIPE_COMPLETION_KEY = 'ff_after_tag_wipe_completion_v1';
export const AFTER_RULE_SYNC_RELOAD_KEY = 'ff_after_rule_sync_completion_v1';
/** Persisted active background job so navigation / reload can resume polling. */
export const ACTIVE_BACKGROUND_JOB_KEY = 'ff_active_bg_job_v1';

const LEAVE_PAGE_SUFFIX = " — you can leave this page; we'll notify you when it finishes.";

/** Short user-facing copy for failed background jobs (never dump raw SQLAlchemy traces). */
export function humanizeBackgroundJobFailure(jobKind: string | null | undefined, raw: unknown): string {
  const kind = String(jobKind || 'rule_sync').trim().toLowerCase();
  const text = String(raw || '').trim();
  const lower = text.toLowerCase();
  const isTagWipe = kind === 'tag_wipe';
  const isSupplier2b = kind === 'supplier_2b_proposals';
  if (
    lower.includes('infailedsqltransaction') ||
    lower.includes('current transaction is aborted') ||
    lower.includes('deadlockdetected') ||
    lower.includes('deadlock detected')
  ) {
    if (isTagWipe) {
      return 'Tag wipe failed due to a database conflict. Completed batches were kept — try again in a moment.';
    }
    if (isSupplier2b) {
      return 'Step 2B proposal generation failed due to a database conflict. Completed pages were kept — try again in a moment.';
    }
    return 'Rule sync failed due to a database conflict. Completed work was kept — try again in a moment.';
  }
  if (lower.includes('timeout') || lower.includes('timed out') || lower.includes('econnaborted')) {
    if (isTagWipe) {
      return 'Tag wipe timed out. Completed batches were kept — try again.';
    }
    if (isSupplier2b) {
      return 'Step 2B proposal generation timed out. Completed pages were kept — try again.';
    }
    return 'Rule sync timed out. Completed work was kept — try again.';
  }
  if (
    lower.includes('worker_lost') ||
    lower.includes('interrupted by a server restart') ||
    lower.includes('deploy or worker recycle')
  ) {
    if (isTagWipe) {
      return 'Tag wipe was interrupted by a server restart. Completed batches were kept — start again if needed.';
    }
    if (isSupplier2b) {
      return 'Step 2B proposal generation was interrupted by a server restart. Completed pages were kept — start again if needed.';
    }
    return 'Rule sync was interrupted by a server restart. Please start the sync again.';
  }
  if (
    lower.includes('diskfull') ||
    lower.includes('no space left on device') ||
    lower.includes('could not extend file')
  ) {
    if (isTagWipe) {
      return 'Tag wipe failed because the server ran out of disk space. Free space on the host, then try again.';
    }
    if (isSupplier2b) {
      return 'Step 2B proposal generation failed because the server ran out of disk space. Free space on the host, then try again.';
    }
    return 'Rule sync failed because the server ran out of disk space. Tags written before the failure were kept — free space on the host, then run Full again.';
  }
  if (text && text.length <= 180 && !lower.includes('psycopg') && !lower.includes('sqlalchemy') && !lower.includes('[sql:')) {
    return text.endsWith('.') ? text : `${text}.`;
  }
  if (isTagWipe) return 'Tag wipe failed on the server. Try again in a moment.';
  if (isSupplier2b) return 'Step 2B proposal generation failed on the server. Try again in a moment.';
  return 'Rule sync failed on the server. Try again in a moment.';
}

export type RuleSyncUiMode = 'full' | 'gaps';

/**
 * Short user-facing progress for snackbars / notifications.
 * Backend `progress_message` stays detailed for logs; UI should not dump batch A/B/C stats
 * or imply Full sync only does the current pipeline step (e.g. cashflow).
 */
export function friendlyBackgroundJobProgressMessage(opts: {
  jobKind?: string | null;
  currentStep?: string | null;
  progressMessage?: string | null;
  cancelRequested?: boolean;
  /** When known from the button that started the job. */
  syncMode?: RuleSyncUiMode | null;
}): string {
  const kind = String(opts.jobKind || 'rule_sync').trim().toLowerCase();
  const step = String(opts.currentStep || '').trim().toLowerCase();
  const raw = String(opts.progressMessage || '').trim();
  const cancel = Boolean(opts.cancelRequested);
  const inferredGaps =
    opts.syncMode === 'gaps' ||
    step.includes('address_missing') ||
    /scanning missing tags|missing-tag remediation|address_missing/i.test(raw);
  const inferredFull = opts.syncMode === 'full' || (!inferredGaps && kind === 'rule_sync');

  let core: string;
  if (kind === 'tag_wipe' || /tag wipe|removing classification tags/i.test(raw)) {
    // Tag wipe is the only flow that deletes all classification tags.
    const wipePct = raw.match(/(\d{1,3})\s*%/);
    core = wipePct
      ? `Removing all classification tags — ${wipePct[1]}%`
      : 'Removing all classification tags on the server';
  } else if (kind === 'supplier_2b_proposals' || /step 2b non-merchant proposals/i.test(raw)) {
    const pct = raw.match(/(\d{1,3})\s*%/);
    const created = raw.match(/([\d,]+)\s+proposed rules created/i);
    if (pct && created) {
      core = `Generating Step 2B supplier proposals — ${pct[1]}% · ${created[1]} proposed rules created`;
    } else if (pct) {
      core = `Generating Step 2B supplier proposals — ${pct[1]}%`;
    } else {
      core = 'Generating Step 2B supplier proposals on the server';
    }
  } else if (inferredGaps) {
    // Gaps: only rows missing a tag at each stage — does not wipe existing tags.
    core =
      'Filling missing tags on uncommitted transactions (Cashflow, Supplier, Category, VAT, Special case)';
  } else if (inferredFull) {
    // Full: pipeline on all uncommitted TXs. Classification uses StripPolicy.NONE — it does
    // not delete all existing tags (unlike the separate tag-wipe action).
    core =
      'Applying Cashflow, Supplier, Category, VAT, and Special-case rules to all uncommitted transactions';
  } else {
    core = 'Running rule sync on the server';
  }

  // Prefer compact server progress when present: "Classification 50% · Overall 58%"
  const pctMatch = raw.match(/^(.+?\d{1,3}%\s*·\s*Overall\s+\d{1,3}%)\s*$/i);
  if (pctMatch) {
    const pctLine = pctMatch[1].trim();
    if (cancel) {
      return `${pctLine} (cancel requested…)`;
    }
    return `${pctLine}${LEAVE_PAGE_SUFFIX}`;
  }

  // Tag wipe: keep leave-page note; % already folded into `core` when server sends it.
  if (cancel) {
    return `${core} (cancel requested…)`;
  }
  return `${core}${LEAVE_PAGE_SUFFIX}`;
}

export type RuleSyncCompletionToastPayload = {
  title: string;
  segments: string[];
  requestId?: string;
  jobId?: string;
};

function tagWipeMetricCount(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && String(v).trim() !== '') {
    const n = Number(String(v).replace(/,/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function readWipeDimensionBucket(raw: unknown): { txs: number; tags: number } | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const txs = tagWipeMetricCount(o.txs);
  const tags = tagWipeMetricCount(o.tags);
  if (txs == null && tags == null) return null;
  return { txs: Math.max(0, txs ?? 0), tags: Math.max(0, tags ?? 0) };
}

function formatWipeDimensionLine(label: string, dim: { txs: number; tags: number }): string {
  // One user-facing count per dimension: distinct TXs that had that tag type cleared.
  // (Raw dual-written row counts are internal; e.g. cashflow heading + type tags.)
  const n = Math.max(0, dim.txs);
  return `${label}: ${n.toLocaleString()} of ${n.toLocaleString()} (100%) tags removed`;
}

/** Normalize API `deleted` / job `result` into the flat aggregate counts object. */
export function coalesceTagWipeAggregatePayload(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const inner = o.aggregate;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return o;
}

export function buildTagWipeCompletionToastFromAggregate(
  jobMessage: string,
  aggregate: unknown
): RuleSyncCompletionToastPayload {
  const raw = String(jobMessage || '').trim();
  let title = (raw || 'Tag wipe completed.').replace(/\s+$/, '');
  const segments: string[] = [];
  const bucket = coalesceTagWipeAggregatePayload(aggregate);
  if (bucket) {
    const dimsRaw =
      bucket.wipe_dimensions && typeof bucket.wipe_dimensions === 'object' && !Array.isArray(bucket.wipe_dimensions)
        ? (bucket.wipe_dimensions as Record<string, unknown>)
        : null;
    const ledgerTotal =
      tagWipeMetricCount(dimsRaw?.ledger_tx_total) ?? tagWipeMetricCount(bucket.ledger_tx_total);
    const txsAffected = tagWipeMetricCount(dimsRaw?.txs_affected);

    if (
      /^tag wipe completed\.?$/i.test(title) &&
      txsAffected != null &&
      txsAffected >= 0 &&
      ledgerTotal != null &&
      ledgerTotal > 0
    ) {
      const pct = Math.min(100, Math.round((100 * txsAffected) / ledgerTotal));
      title = `Tag wipe completed: ${txsAffected.toLocaleString()} of ${ledgerTotal.toLocaleString()} (${pct}%) transactions had all of their tags removed...`;
    } else if (txsAffected != null && txsAffected >= 0 && /^tag wipe completed\.?$/i.test(title)) {
      title = `Tag wipe completed: ${txsAffected.toLocaleString()} transactions had all of their tags removed...`;
    } else if (ledgerTotal != null && ledgerTotal > 0 && /^tag wipe completed\.?$/i.test(title)) {
      title = `Tag wipe completed: ${ledgerTotal.toLocaleString()} of ${ledgerTotal.toLocaleString()} (100%) transactions had all of their tags removed...`;
    }

    const dimOrder: Array<[string, string]> = [
      ['Cashflow', 'cashflow'],
      ['Supplier', 'supplier'],
      ['Category', 'category'],
      ['VAT', 'vat']
    ];
    if (dimsRaw) {
      for (const [label, key] of dimOrder) {
        const dim = readWipeDimensionBucket(dimsRaw[key]);
        if (!dim || dim.txs <= 0) continue;
        segments.push(formatWipeDimensionLine(label, dim));
      }
    }
    // Table-level counters (revolut_transaction_category_tags, tx_state, …) stay in the job
    // payload for support/debug — not shown in the user toast.
  }
  const t = title.endsWith('.') || title.endsWith('…') || title.endsWith('...') ? title : `${title}.`;
  return { title: t, segments };
}

function formatRuleSyncStageDuration(seconds: unknown): string {
  const n = typeof seconds === 'number' ? seconds : Number(seconds);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 60) return (n >= 10 ? `${Math.round(n)}` : n.toFixed(1)) + 's';
  const m = Math.floor(n / 60);
  const s = Math.round(n - m * 60);
  return `${m}m ${s}s`;
}

export function buildSupplier2bProposalCompletionToastFromJob(
  job: Record<string, unknown>
): RuleSyncCompletionToastPayload {
  const result =
    job?.result && typeof job.result === 'object' && !Array.isArray(job.result)
      ? (job.result as Record<string, unknown>)
      : {};
  const n = (key: string) => {
    const v = Number(result[key] ?? 0);
    return Number.isFinite(v) ? v : 0;
  };
  const created = n('created');
  const nonMerchant = n('created_non_merchant_style');
  const scanned = n('transactions_scanned');
  const residualRaw = result.residual_gap_tx_count;
  const residual =
    residualRaw == null || residualRaw === ''
      ? null
      : Number.isFinite(Number(residualRaw))
        ? Number(residualRaw)
        : null;

  const rawMsg = String(job?.message || '').trim();
  let title = (rawMsg || 'Step 2B non-merchant proposal sweep finished.').replace(/\s+$/, '');
  // Prefer a short headline; ignore legacy one-line messages that stuffed stats after ":".
  if (/^step 2b non-merchant proposal sweep finished/i.test(title)) {
    title = 'Step 2B non-merchant proposal sweep finished.';
  } else if (!title.endsWith('.')) {
    title = `${title}.`;
  }

  const segments: string[] = [];
  if (created > 0) {
    segments.push(
      nonMerchant > 0 && nonMerchant !== created
        ? `Created ${created.toLocaleString()} proposed rules (${nonMerchant.toLocaleString()} non-merchant).`
        : `Created ${created.toLocaleString()} proposed rules.`
    );
  } else {
    segments.push('No new proposed rules were created.');
  }
  if (scanned > 0) {
    segments.push(`Scanned ${scanned.toLocaleString()} ledger rows.`);
  }
  if (residual != null) {
    segments.push(
      residual > 0
        ? `${residual.toLocaleString()} transactions still have no supplier after this sweep.`
        : 'Every scanned transaction now has supplier coverage from a rule or proposal.'
    );
  }

  const skipLines: Array<[string, number]> = [
    ['Already had a supplier', n('skipped_not_missing_supplier')],
    ['Existing supplier rule matched', n('skipped_existing_rule')],
    ['Pending proposal already matches', n('skipped_matching_pending_proposal')],
    ['Proposal already exists for that transaction', n('skipped_existing_proposal_for_tx')],
    ['No non-merchant candidate', n('skipped_no_non_merchant_candidate')],
    ['No supplier candidate', n('skipped_no_supplier_candidate')],
    ['No match text', n('skipped_no_match_text')]
  ];
  for (const [label, count] of skipLines) {
    if (count > 0) {
      segments.push(`${label}: ${count.toLocaleString()} skipped.`);
    }
  }

  return { title, segments };
}

function formatRuleSyncNowTaggedLine(label: string, tagged: number, scopeTotal: number): string {
  const n = Math.max(0, tagged);
  const denom = Math.max(0, scopeTotal);
  const pct = denom > 0 ? Math.min(100, Math.round((100 * n) / denom)) : n === 0 ? 100 : 0;
  return `${label}: ${n.toLocaleString()} of ${denom.toLocaleString()} (${pct}%) now tagged`;
}

/** Final Merchant (2A) vs Other (2B) supplier coverage split. */
function formatSupplierMerchantOtherSplit(merchant: number, other: number): string {
  return (
    `Merchant: ${Math.max(0, merchant).toLocaleString()} · ` +
    `Other: ${Math.max(0, other).toLocaleString()}`
  );
}

function readSupplierStageTaggedCount(
  stage: Record<string, unknown>,
  preferKeys: string[]
): number {
  const nu = (x: unknown) => Number(x ?? 0);
  for (const key of preferKeys) {
    const n = nu(stage[key]);
    if (n > 0) return n;
  }
  return 0;
}

/** Canonical tag dimensions for missing-permutation labels (matches backend coverage). */
const COVERAGE_TAG_DIMS = ['Cashflow', 'Supplier', 'Category', 'VAT'] as const;

/** All 15 non-empty subsets of the four tags, in stable dim order within each label. */
function allCoverageMissingComboLabels(): string[] {
  const labels: string[] = [];
  const n = COVERAGE_TAG_DIMS.length;
  for (let mask = 1; mask < 1 << n; mask += 1) {
    const parts: string[] = [];
    for (let i = 0; i < n; i += 1) {
      if (mask & (1 << i)) parts.push(COVERAGE_TAG_DIMS[i]);
    }
    labels.push(parts.join(' + '));
  }
  return labels;
}

const ALL_COVERAGE_MISSING_COMBO_LABELS = allCoverageMissingComboLabels();

function normalizeCoverageMissingComboKey(raw: string): string {
  const parts = String(raw || '')
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const lc = p.toLowerCase();
      if (lc === 'cash flow' || lc === 'cashflow') return 'Cashflow';
      if (lc === 'vat rate' || lc === 'vat') return 'VAT';
      if (lc === 'supplier') return 'Supplier';
      if (lc === 'category') return 'Category';
      return p;
    });
  const set = new Set(parts);
  return COVERAGE_TAG_DIMS.filter((d) => set.has(d)).join(' + ');
}

function formatMissingComboLabel(label: string): string {
  const t = String(label || '').trim();
  if (!t) return t;
  // Single-dimension gaps read as "VAT only"; multi-tag gaps keep "A + B".
  if (!t.includes('+')) {
    return t.endsWith(' only') ? t : `${t} only`;
  }
  return t;
}

/** Non-zero missing permutations of the four tags (dynamic; zeros omitted). */
function orderedMissingComboEntries(
  missingComboCounts: Record<string, number>
): Array<[string, number]> {
  const normalized: Record<string, number> = {};
  for (const [raw, count] of Object.entries(missingComboCounts || {})) {
    const key = normalizeCoverageMissingComboKey(raw);
    if (!key) continue;
    normalized[key] = (normalized[key] || 0) + (Number(count) || 0);
  }
  const entries: Array<[string, number]> = [];
  for (const label of ALL_COVERAGE_MISSING_COMBO_LABELS) {
    const count = normalized[label] || 0;
    if (count > 0) entries.push([label, count]);
  }
  entries.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return entries;
}

function readTelemetryTotalSeconds(r: Record<string, unknown>): number | null {
  const tel = r.telemetry as Record<string, unknown> | undefined;
  if (tel && tel.total_seconds != null && Number.isFinite(Number(tel.total_seconds))) {
    return Number(tel.total_seconds);
  }
  return null;
}

/** Shared Full/Gaps human toast body (coverage lines + merchant/other + missing combos). */
function buildCoverageStyleCompletionToast(opts: {
  mode: 'full' | 'gaps';
  /** Denominator for Cashflow/Supplier/Category/VAT / missing-combo % (Tx Review coverage cohort). */
  scopeTotal: number;
  /** Title “processed” count; may include declined/reverted txs excluded from coverage. */
  processedTotal?: number;
  cashflowNow: number;
  supplierNow: number;
  merchantTagged: number;
  otherTagged: number;
  categoryNow: number;
  vatNow: number;
  completeAll4: number;
  missingComboCounts: Record<string, number>;
  totalSeconds: number | null;
}): RuleSyncCompletionToastPayload {
  const scopeTotal = Math.max(0, opts.scopeTotal);
  const processedTotal = Math.max(0, opts.processedTotal ?? scopeTotal);
  const segments = [
    formatRuleSyncNowTaggedLine('Cashflow', opts.cashflowNow, scopeTotal),
    formatRuleSyncNowTaggedLine('Supplier', opts.supplierNow, scopeTotal),
    formatSupplierMerchantOtherSplit(opts.merchantTagged, opts.otherTagged),
    formatRuleSyncNowTaggedLine('Category', opts.categoryNow, scopeTotal),
    formatRuleSyncNowTaggedLine('VAT', opts.vatNow, scopeTotal),
    // Blank line before the all-four / missing-combo block.
    ''
  ];

  if (scopeTotal > 0 || opts.completeAll4 > 0) {
    const complete = Math.max(0, opts.completeAll4);
    const completePct =
      scopeTotal > 0 ? Math.min(100, Math.round((100 * complete) / scopeTotal)) : complete === 0 ? 100 : 0;
    segments.push(
      `Has all four tags: ${complete.toLocaleString()} of ${scopeTotal.toLocaleString()} (${completePct}%)`
    );
    // Every non-zero missing permutation of Cashflow/Supplier/Category/VAT.
    for (const [label, count] of orderedMissingComboEntries(opts.missingComboCounts)) {
      const pct = scopeTotal > 0 ? Math.min(100, Math.round((100 * count) / scopeTotal)) : 0;
      segments.push(
        `Missing '${formatMissingComboLabel(label)}': ${count.toLocaleString()} (${pct}%)`
      );
    }
  }

  const timePart =
    opts.totalSeconds != null ? ` in ${formatRuleSyncStageDuration(opts.totalSeconds)}` : '';
  const modeLabel = opts.mode === 'gaps' ? 'Gaps' : 'Full';
  const title =
    `Rule sync (${modeLabel}) complete: ${processedTotal.toLocaleString()} of ${processedTotal.toLocaleString()} (100%) ` +
    `uncommitted transactions processed${timePart}...`;

  return { title, segments };
}

function readMerchantOtherFromResult(r: Record<string, unknown>): { merchant: number; other: number } {
  const cov = (r.tag_coverage as Record<string, unknown>) || {};
  const nu = (x: unknown) => Number(x ?? 0);
  // Prefer final coverage split (Gaps often adds 0 supplier tags when already complete).
  const covMerchant = nu(cov.with_merchant_supplier);
  const covOther = nu(cov.with_other_supplier);
  if (covMerchant > 0 || covOther > 0 || (nu(cov.total) > 0 && 'with_merchant_supplier' in cov)) {
    return { merchant: Math.max(0, covMerchant), other: Math.max(0, covOther) };
  }
  const missing = (r.address_missing as Record<string, unknown>) || {};
  const s2a =
    (r.supplier_2a as Record<string, unknown>) ||
    (missing.supplier_2a as Record<string, unknown>) ||
    {};
  const s2b =
    (r.supplier_2b as Record<string, unknown>) ||
    (r.supplier as Record<string, unknown>) ||
    (missing.supplier_refresh as Record<string, unknown>) ||
    {};
  return {
    merchant: readSupplierStageTaggedCount(s2a, [
      'canonical_rows_created',
      'tags_updated',
      'txs_tagged_scope'
    ]),
    other: readSupplierStageTaggedCount(s2b, [
      'canonical_rows_created',
      'total_updated',
      'tags_updated'
    ])
  };
}

/** Gaps completion toast — same layout as Full (coverage + merchant/other + missing combos). */
function buildGapsCompletionToastFromResult(
  r: Record<string, unknown>,
  _rawMsg: string
): RuleSyncCompletionToastPayload {
  const missing = (r.address_missing as Record<string, unknown>) || {};
  const initial = (missing.initial_missing as Record<string, unknown>) || {};
  const final = (missing.final_missing as Record<string, unknown>) || {};
  const cov = (r.tag_coverage as Record<string, unknown>) || {};
  const nu = (x: unknown) => Number(x ?? 0);
  const { merchant, other } = readMerchantOtherFromResult(r);

  const covTotal = nu(cov.total);
  if (covTotal > 0) {
    return buildCoverageStyleCompletionToast({
      mode: 'gaps',
      scopeTotal: covTotal,
      cashflowNow: nu(cov.with_cashflow),
      supplierNow: nu(cov.with_supplier),
      merchantTagged: merchant,
      otherTagged: other,
      categoryNow: nu(cov.with_category),
      vatNow: nu(cov.with_vat),
      completeAll4: nu(cov.complete_all_4),
      missingComboCounts: Object.fromEntries(
        Object.entries((cov.missing_combo_counts as Record<string, unknown>) || {}).map(([k, v]) => [
          k,
          nu(v)
        ])
      ),
      totalSeconds: readTelemetryTotalSeconds(r)
    });
  }

  // Older Gaps jobs without tag_coverage: approximate from final missing vs scanned cohort.
  const scanned = nu(initial.scanned_rows) || nu(initial.total_rows) || nu(initial.any);
  const scopeTotal = scanned > 0 ? scanned : 0;
  const heading =
    (missing.heading as Record<string, unknown>) || (r.heading as Record<string, unknown>) || {};
  const headingAdded = nu(heading.total_created) || nu(heading.total_txs_tagged);
  const headingHad = nu(initial.heading);
  const headingStill = nu(final.heading);
  const cashflowNow =
    scopeTotal > 0
      ? Math.max(0, scopeTotal - (headingStill > 0 ? headingStill : Math.max(0, headingHad - headingAdded)))
      : headingAdded;
  const supplierNow =
    scopeTotal > 0 ? Math.max(0, scopeTotal - nu(final.supplier)) : merchant + other;
  const categoryNow =
    scopeTotal > 0
      ? Math.max(0, scopeTotal - nu(final.category))
      : nu((missing.classification as Record<string, unknown> | undefined)?.total_created);
  const vatNow =
    scopeTotal > 0
      ? Math.max(0, scopeTotal - nu(final.vat))
      : nu((missing.vat_reapply as Record<string, unknown> | undefined)?.tags_updated);
  const anyStill = nu(final.any);
  const completeAll4 = scopeTotal > 0 ? Math.max(0, scopeTotal - Math.max(0, anyStill)) : 0;

  return buildCoverageStyleCompletionToast({
    mode: 'gaps',
    scopeTotal,
    cashflowNow,
    supplierNow,
    merchantTagged: merchant,
    otherTagged: other,
    categoryNow,
    vatNow,
    completeAll4,
    missingComboCounts: {},
    totalSeconds: readTelemetryTotalSeconds(r)
  });
}

function buildFullCompletionToastFromResult(
  r: Record<string, unknown>,
  _rawMsg: string
): RuleSyncCompletionToastPayload {
  const h = (r.heading as Record<string, unknown>) || {};
  const s2a = (r.supplier_2a as Record<string, unknown>) || {};
  const s2b = (r.supplier_2b as Record<string, unknown>) || (r.supplier as Record<string, unknown>) || {};
  const c = (r.classification as Record<string, unknown>) || {};
  const v = (r.vat as Record<string, unknown>) || {};
  const sp = (r.special_case as Record<string, unknown>) || {};
  const cov = (r.tag_coverage as Record<string, unknown>) || {};
  const nu = (x: unknown) => Number(x ?? 0);
  const { merchant, other } = readMerchantOtherFromResult(r);

  const created1 = nu(h.total_created);
  let tagged1 = nu(h.total_txs_tagged);
  if (tagged1 <= 0) tagged1 = created1;

  let txAll = nu(h.total_reprocessed);
  for (const n of [
    nu(s2a.txs_in_scope),
    nu(s2b.total_reprocessed),
    nu(c.total_reprocessed),
    nu(v.total_reprocessed),
    nu(sp.total_reprocessed),
    nu(cov.total)
  ]) {
    if (n > txAll) txAll = n;
  }
  const covTotal = nu(cov.total);
  const hasCoverage = covTotal > 0;
  // Tag % lines must use the coverage cohort (completed / Tx Review rows), not the full
  // processed ledger — declined/reverted txs are synced but excluded from tag_coverage.
  const scopeTotal = hasCoverage ? covTotal : txAll > 0 ? txAll : 0;
  const processedTotal = txAll > 0 ? txAll : scopeTotal;

  const withCashflow = hasCoverage ? nu(cov.with_cashflow) : tagged1;
  const cashflowNow = withCashflow > 0 ? withCashflow : tagged1;

  const withSupplier = hasCoverage ? nu(cov.with_supplier) : merchant + other;
  const supplierNow = withSupplier > 0 ? withSupplier : Math.min(scopeTotal, merchant + other);

  let clsTxsWithTag = nu(c.total_txs_with_granular_tag);
  if (clsTxsWithTag <= 0) clsTxsWithTag = nu(c.total_txs_with_new_tags);
  const withCategory = hasCoverage ? nu(cov.with_category) : clsTxsWithTag;
  const categoryNow = withCategory > 0 ? withCategory : clsTxsWithTag;

  const withVat = hasCoverage ? nu(cov.with_vat) : nu(v.total_txs_updated) || nu(v.total_updated);
  const vatNow = withVat;

  const comboCounts: Record<string, number> = {};
  if (hasCoverage) {
    for (const [k, vCount] of Object.entries(
      (cov.missing_combo_counts as Record<string, unknown>) || {}
    )) {
      comboCounts[k] = nu(vCount);
    }
  }

  return buildCoverageStyleCompletionToast({
    mode: 'full',
    scopeTotal,
    processedTotal,
    cashflowNow,
    supplierNow,
    merchantTagged: merchant,
    otherTagged: other,
    categoryNow,
    vatNow,
    completeAll4: hasCoverage ? nu(cov.complete_all_4) : 0,
    missingComboCounts: comboCounts,
    totalSeconds: readTelemetryTotalSeconds(r)
  });
}

export function buildRuleSyncCompletionToastFromJob(job: Record<string, unknown>): RuleSyncCompletionToastPayload {
  const rawMsg = String(job?.message || '').trim();
  const result = job?.result;
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    const r = result as Record<string, unknown>;
    if (r.address_missing && typeof r.address_missing === 'object') {
      return buildGapsCompletionToastFromResult(r, rawMsg);
    }
    return buildFullCompletionToastFromResult(r, rawMsg);
  }
  const lines = rawMsg
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length > 1) {
    return { title: lines[0], segments: lines.slice(1) };
  }
  const fallback = rawMsg || 'Rule sync completed.';
  return { title: 'Rule sync completed', segments: [fallback] };
}

export function pickRequestIdFromJobPollResponse(res: { headers?: unknown } | null | undefined): string | undefined {
  const h = res?.headers;
  if (!h || typeof h !== 'object') return undefined;
  const rec = h as Record<string, string>;
  const v = String(rec['x-request-id'] ?? rec['X-Request-ID'] ?? '').trim();
  return v || undefined;
}

export function completionDiagnosticsFromJobPoll(
  res: { headers?: unknown } | null | undefined,
  activeJobId: string,
  job: Record<string, unknown>
): { requestId?: string; jobId?: string } {
  const requestId = pickRequestIdFromJobPollResponse(res);
  const jid = String(
    (job as { id?: unknown }).id ?? (job as { job_id?: unknown }).job_id ?? activeJobId ?? ''
  ).trim();
  return { requestId, jobId: jid || undefined };
}
